import { useMediaQuery } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { LoadingContext, M_StateActions, MTabsInpage, NavigateContext } from 'src/M_Components';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import TenderInQuotation from '../Tenders/TenderInQuotation';
import TenderInAnalysis from '../Tenders/TenderInAnalysis';
import TenderFinished from '../Tenders/TenderFinished';
import SpotTenderConfig from './SpotTenderConfig';
import { hasPermission } from 'src/components/utils/permissionsHelper';

// unified tabs styling
import 'src/pages/tabsInpage.css';

export default function SpotTenderPage({ roles, onCreateTab, toastMessages, handleTabClose }) {
    const ctx = useContext(LoadingContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const matches = useMediaQuery('(min-width:900px)');
    const M_State = useSelector((state) => state.StateKeyValue);
    const location = useLocation();
    const { renameTab } = useContext(NavigateContext);
    const selectedTab = useSelector((s) => s.StateKeyValue.find((o) => o.key === 'selectedTab')?.value);

    // Opens the tab with the last selected menu item
    // If no session data is found, it defaults to the first tab (menu 1)
    const [menu, setMenu] = useState(1);
    const [refresh, setRefresh] = useState(false);
    const [id, setId] = useState(() => M_State.find((obj) => obj.key === 'selectedTab')?.value);

    //============================================================================================================
    // EFFECT
    // Rename tab on language change
    //============================================================================================================
    useEffect(() => {
        if (!selectedTab) return;
        renameTab(String(selectedTab), t('SpotTenders'));
    }, [t, selectedTab, renameTab]);

    //============================================================================================================
    // Upsert session data for the current tab
    //============================================================================================================
    const upsertSession = (tabId, kv) => {
        const current = M_State.find((o) => o.key === tabId)?.value || [];
        const filtered = current.filter((e) => e.key !== kv.key);
        dispatch(M_StateActions.addM_State({ key: tabId, value: [...filtered, kv] }));
    };

    //============================================================================================================
    // Function to retrieve session storage data based on session ID
    //============================================================================================================
    const getSessionStorageData = (sessionID) => {
        if (!sessionID) return;

        ctx.setIsLoad(true);

        const session = M_State.find((obj) => obj.key === sessionID)?.value;
        if (session) {
            const m = session.find((x) => x.key === 'menu')?.value;
            const ref = session.find((x) => x.key === 'refresh')?.value;

            setMenu(typeof m === 'number' ? m : 1);

            if (ref !== undefined) {
                setRefresh(ref);
            }
        } else {
            setMenu(1);
        }

        ctx.setIsLoad(false);
    };

    //============================================================================================================
    // EFFECT
    // Retrieve session data when the component mounts or when the pathname changes
    //============================================================================================================
    useEffect(() => {
        if (!id) return;

        getSessionStorageData(id);
        setRefresh((r) => !r);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    //============================================================================================================
    // EFFECT
    // useEffect for selectedTab changes
    //============================================================================================================
    useEffect(() => {
        const newId = M_State.find((obj) => obj.key === 'selectedTab')?.value;

        if (newId && newId !== id) {
            setId(newId);
            getSessionStorageData(newId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [M_State]);

    //============================================================================================================
    // EFFECT
    // Persist current selected menu per tab
    //============================================================================================================
    useEffect(() => {
        if (!id) return;

        upsertSession(id, { key: 'menu', value: menu });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, menu]);

    //============================================================================================================
    // EFFECT
    // Persist refresh flag per tab
    //============================================================================================================
    useEffect(() => {
        if (!id) return;

        upsertSession(id, { key: 'refresh', value: refresh });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, refresh]);

    //============================================================================================================
    // Function to determine which tabs to display based on user permissions
    //============================================================================================================
    const allowedTabs = () => {
        const tabArray = [
            { id: 1, labelTab: t('SpotTendersConfiguration'), iconTab: <i className="pi pi-wrench"></i> },
        ];

        if (hasPermission(roles, 'Tenders', 'Update') || hasPermission(roles, 'Tenders', 'Admin')) {
            tabArray.push(
                { id: 2, labelTab: t('SpotTendersInQuotation'), iconTab: <i className="pi pi-chart-line"></i> },
                { id: 3, labelTab: t('SpotTendersInAnalysis'), iconTab: <i className="pi pi-bullseye"></i> },
                { id: 4, labelTab: t('SpotTendersFinished'), iconTab: <i className="pi pi-check-square"></i> }
            );
        }

        return tabArray;
    };

    return (
        <div className="tabsInpage divGlobalSettings">
            <div className="tabsInpage__title titleSettings" style={{ color: ctx.secondaryColor }}>
                {t('SpotTenders')}
            </div>

            <div className="tabsInpage__body">
                <div className="tabsInpage__sidebar">
                    <MTabsInpage
                        type={matches ? 'column' : 'tabs'}
                        renderTabs={allowedTabs()}
                        firstTabOpen={menu}
                        onGetTab={(obj) => setMenu(obj.id)}
                        fixedArea={false}
                    />
                </div>

                <div className="tabsInpage__content settingsPageMainDiv">
                    {menu === 1 && (
                        <SpotTenderConfig
                            roles={roles}
                            onCreateTab={onCreateTab}
                            toastMessages={toastMessages}
                            handleTabClose={handleTabClose}
                        />
                    )}

                    {menu === 2 && (
                        <TenderInQuotation
                            tenderTypeId={1}
                            mode="spot"
                            roles={roles}
                            onCreateTab={onCreateTab}
                            toastMessages={toastMessages}
                            handleTabClose={handleTabClose}
                        />
                    )}

                    {menu === 3 && (
                        <TenderInAnalysis
                            tenderTypeId={1}
                            mode="spot"
                            roles={roles}
                            onCreateTab={onCreateTab}
                            toastMessages={toastMessages}
                            handleTabClose={handleTabClose}
                        />
                    )}

                    {menu === 4 && (
                        <TenderFinished
                            tenderTypeId={1}
                            mode="spot"
                            roles={roles}
                            onCreateTab={onCreateTab}
                            toastMessages={toastMessages}
                            handleTabClose={handleTabClose}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}